(() => {
  'use strict';

  const GLOBAL_KEY = '__puffyConcept7Shop';
  const DEFAULT_SORT = 'best-selling';
  const RESULT_IMAGE_SIZES = '(max-width: 749px) 50vw, (max-width: 1099px) 33vw, 25vw';
  const FRIENDLY_PARAMS = ['type', 'mood', 'strain', 'under20', 'tier', 'sort'];
  const PRIME_DISMISS_KEY = 'pp:puffy-prime-strip:dismissed:v1';
  const SELECTORS = {
    root: '[data-pp-shop]',
    finder: '[data-pp-shop-finder]',
    filter: '[data-pp-shop-filter]',
    tier: '[data-pp-shop-tier]',
    tierRegion: '[data-pp-shop-tier-region]',
    moodsToggle: '[data-pp-shop-moods-toggle]',
    extraMood: '[data-pp-shop-extra-mood]',
    apply: '[data-pp-shop-apply]',
    applyCount: '[data-pp-shop-apply-count]',
    reset: '[data-pp-shop-reset]',
    results: '[data-pp-shop-results]',
    grid: '[data-pp-shop-grid]',
    status: '[data-pp-shop-status]',
    resultCount: '[data-pp-shop-result-count]',
    headingMain: '[data-pp-shop-heading-main]',
    headingAccent: '[data-pp-shop-heading-accent]',
    summary: '[data-pp-shop-summary]',
    chips: '[data-pp-shop-chips]',
    sort: '[data-pp-shop-sort]',
    more: '[data-pp-shop-more]',
    moreLabel: '[data-pp-shop-more-label]',
    sentinel: '[data-pp-shop-sentinel]',
    sentinelLabel: '[data-pp-shop-sentinel-label]',
    progress: '[data-pp-shop-progress]',
    empty: '[data-pp-shop-empty]',
    error: '[data-pp-shop-error]',
    retry: '[data-pp-shop-retry]',
    loading: '[data-pp-shop-loading]',
    filterOpen: '[data-pp-shop-filter-open]',
    filterDialog: '[data-pp-shop-filter-dialog]',
    filterClose: '[data-pp-shop-filter-close]',
    filterCount: '[data-pp-shop-filter-count]',
    primeStrip: '[data-pp-prime-strip]',
    primeOpen: '[data-pp-prime-open]',
    primeDismiss: '[data-pp-prime-dismiss]',
    primeDialog: '[data-pp-prime-dialog]',
    primeClose: '[data-pp-prime-close]',
    primeVideo: '[data-pp-prime-video]'
  };

  if (window[GLOBAL_KEY] && typeof window[GLOBAL_KEY].initializeAll === 'function') {
    window[GLOBAL_KEY].initializeAll(document);
    return;
  }

  const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();

  const cloneFilters = (filters) => ({
    type: filters.type,
    mood: filters.mood,
    strain: filters.strain,
    tier: filters.type === 'flower' ? filters.tier : ''
  });

  const filtersEqual = (left, right) => (
    left.type === right.type &&
    left.mood === right.mood &&
    left.strain === right.strain &&
    left.tier === right.tier
  );

  const isAbortError = (error) => Boolean(error && error.name === 'AbortError');

  const parseCount = (element, fallback) => {
    if (!element) return { value: fallback, known: false };

    const match = normalizeText(element.textContent).match(/\d[\d.,\s\u00a0\u202f]*/);
    if (!match) return { value: fallback, known: false };

    const digits = match[0].replace(/\D/g, '');
    const value = Number.parseInt(digits, 10);
    return Number.isFinite(value) ? { value, known: true } : { value: fallback, known: false };
  };

  const stripScripts = (scope) => {
    scope.querySelectorAll('script').forEach((script) => {
      if (script.parentNode) script.parentNode.removeChild(script);
    });
    scope.querySelectorAll('template').forEach((template) => stripScripts(template.content));
  };

  const rewriteImageSizes = (scope) => {
    scope.querySelectorAll('img[sizes]').forEach((image) => image.setAttribute('sizes', RESULT_IMAGE_SIZES));
    scope.querySelectorAll('template').forEach((template) => rewriteImageSizes(template.content));
  };

  const cloneProductCard = (card) => {
    const clone = card.cloneNode(true);
    stripScripts(clone);
    clone.querySelectorAll('img[loading="eager"]').forEach((image) => image.setAttribute('loading', 'lazy'));
    rewriteImageSizes(clone);
    clone.setAttribute('data-pp-shop-card', '');
    return clone;
  };

  const productCardIsPuffyPrime = (card) => Array.from(card.querySelectorAll('a[href]')).some((link) => {
    const href = (link.getAttribute('href') || '').split(/[?#]/)[0].replace(/\/$/, '');
    return href.endsWith('/products/puffy-prime');
  });

  const rootsWithin = (scope) => {
    const roots = [];
    if (!scope || typeof scope.querySelectorAll !== 'function') return roots;
    if (typeof scope.matches === 'function' && scope.matches(SELECTORS.root)) roots.push(scope);
    scope.querySelectorAll(SELECTORS.root).forEach((root) => roots.push(root));
    return roots;
  };

  const initializeShop = (root) => {
    if (root.dataset.ppShopEnhanced === 'true') return;
    if (
      typeof window.fetch !== 'function' ||
      typeof window.URL !== 'function' ||
      typeof window.DOMParser !== 'function'
    ) return;

    const form = root.querySelector(SELECTORS.finder);
    const results = root.querySelector(SELECTORS.results);
    const grid = root.querySelector(SELECTORS.grid);
    if (!form || !results || !grid) return;

    const elements = {
      form,
      results,
      grid,
      tierRegion: root.querySelector(SELECTORS.tierRegion),
      moodsToggle: root.querySelector(SELECTORS.moodsToggle),
      apply: root.querySelector(SELECTORS.apply),
      applyCount: root.querySelector(SELECTORS.applyCount),
      resets: Array.from(root.querySelectorAll(SELECTORS.reset)),
      status: root.querySelector(SELECTORS.status),
      resultCount: root.querySelector(SELECTORS.resultCount),
      headingMain: root.querySelector(SELECTORS.headingMain),
      headingAccent: root.querySelector(SELECTORS.headingAccent),
      summary: root.querySelector(SELECTORS.summary),
      chips: root.querySelector(SELECTORS.chips),
      sort: root.querySelector(SELECTORS.sort),
      more: root.querySelector(SELECTORS.more),
      moreLabel: root.querySelector(SELECTORS.moreLabel),
      sentinel: root.querySelector(SELECTORS.sentinel),
      sentinelLabel: root.querySelector(SELECTORS.sentinelLabel),
      progress: root.querySelector(SELECTORS.progress),
      empty: root.querySelector(SELECTORS.empty),
      error: root.querySelector(SELECTORS.error),
      retry: root.querySelector(SELECTORS.retry),
      loading: root.querySelector(SELECTORS.loading),
      filterOpen: root.querySelector(SELECTORS.filterOpen),
      filterDialog: root.querySelector(SELECTORS.filterDialog),
      filterClose: root.querySelector(SELECTORS.filterClose),
      filterCount: root.querySelector(SELECTORS.filterCount),
      primeStrip: root.querySelector(SELECTORS.primeStrip),
      primeOpen: root.querySelector(SELECTORS.primeOpen),
      primeDismiss: root.querySelector(SELECTORS.primeDismiss),
      primeDialog: root.querySelector(SELECTORS.primeDialog),
      primeClose: root.querySelector(SELECTORS.primeClose),
      primeVideo: root.querySelector(SELECTORS.primeVideo),
      tierInputs: Array.from(form.querySelectorAll(SELECTORS.tier)),
      extraMoods: Array.from(form.querySelectorAll(SELECTORS.extraMood))
    };
    const allowedTypes = new Set(
      Array.from(form.elements)
        .filter((control) => control.name === 'product_type')
        .map((control) => control.value)
    );
    const allowedTiers = new Set(elements.tierInputs.map((control) => control.value));
    allowedTypes.add('all');
    allowedTiers.add('');

    const listeners = [];
    const originalDisabled = new WeakMap();
    const cache = new Map();
    let initialObserver = null;
    let infiniteObserver = null;
    let infiniteRearmFrame = 0;
    let stickyHeaderObserver = null;
    let stickyHeaderFrame = 0;
    let lastFilterTrigger = null;
    let restoreFilterFocus = true;
    let preserveFilterChoicesOnClose = false;
    let lastPrimeTrigger = null;
    let restorePrimeFocus = true;
    const ageVerifier = document.getElementById('AgeVerifier');
    const hasAgeCookie = () => document.cookie.split(';').some((cookie) => cookie.trim().startsWith('concept:age-verifier='));
    let ageGateResolved = !ageVerifier || hasAgeCookie();
    const pageSizeValue = Number.parseInt(root.dataset.pageSize, 10);
    const pageSize = Number.isFinite(pageSizeValue) && pageSizeValue > 0 ? pageSizeValue : 4;
    const catalogDefaultSort = root.dataset.catalogDefaultSort || 'title-ascending';
    const excludePuffyPrime = root.dataset.excludePuffyPrime === 'true';
    let renderURL;
    let allURL;

    try {
      renderURL = new URL(root.dataset.sectionRenderUrl || '/collections/all?section_id=main-collection', window.location.origin);
      allURL = new URL(root.dataset.allUrl || renderURL.pathname, window.location.origin);
    } catch (error) {
      return;
    }

    const sectionId = renderURL.searchParams.get('section_id') || 'main-collection';
    const state = {
      destroyed: false,
      pending: null,
      applied: null,
      sort: DEFAULT_SORT,
      appliedSort: DEFAULT_SORT,
      preflightTimer: 0,
      announceTimer: 0,
      preflightController: null,
      mainController: null,
      moreController: null,
      initialController: null,
      preflightToken: 0,
      mainToken: 0,
      moreToken: 0,
      initialToken: 0,
      preflightBusy: false,
      mainBusy: false,
      moreBusy: false,
      currentTotal: 0,
      countKnown: false,
      currentNextURL: '',
      currentQueryURL: '',
      renderGeneration: 0,
      loadedPages: new Set(),
      retryAction: null,
      moodsExpanded: false,
      needsInitialFetch: true
    };

    const rememberDisabled = (element) => {
      if (!element || !('disabled' in element) || originalDisabled.has(element)) return;
      originalDisabled.set(element, Boolean(element.disabled));
    };

    [elements.apply, elements.sort, elements.more, elements.retry]
      .concat(elements.tierInputs)
      .forEach(rememberDisabled);

    const setDisabled = (element, disabled) => {
      if (!element || !('disabled' in element)) return;
      const wasDisabled = originalDisabled.get(element) || false;
      element.disabled = wasDisabled || Boolean(disabled);
    };

    const on = (target, type, handler, options) => {
      if (!target) return;
      target.addEventListener(type, handler, options || false);
      listeners.push({ target, type, handler, options: options || false });
    };

    if (elements.primeStrip && !(window.Shopify && window.Shopify.designMode)) {
      try {
        if (window.sessionStorage.getItem(PRIME_DISMISS_KEY) === 'true') elements.primeStrip.hidden = true;
      } catch (error) {
        /* Storage may be blocked; the strip can remain visible. */
      }
    }

    const syncStickyHeaderOffset = () => {
      stickyHeaderFrame = 0;
      const header = document.querySelector('header.header[is="sticky-header"]');
      const headerSection = header && header.closest('.header-section');

      if (!header || !headerSection || !headerSection.classList.contains('header-sticky')) {
        root.style.removeProperty('--pps-sticky-header-height');
        return;
      }

      const rect = header.getBoundingClientRect();
      const offset = rect.bottom > 0 ? Math.ceil(rect.bottom) : 0;
      root.style.setProperty('--pps-sticky-header-height', `${offset}px`);
    };

    const scheduleStickyHeaderOffset = () => {
      if (stickyHeaderFrame) return;
      stickyHeaderFrame = window.requestAnimationFrame(syncStickyHeaderOffset);
    };

    const stickyHeader = document.querySelector('header.header[is="sticky-header"]');
    if (stickyHeader) {
      syncStickyHeaderOffset();
      on(window, 'scroll', scheduleStickyHeaderOffset, { passive: true });
      on(window, 'resize', scheduleStickyHeaderOffset, { passive: true });
      on(document, 'header:scrolled', scheduleStickyHeaderOffset);
      if (typeof window.ResizeObserver === 'function') {
        stickyHeaderObserver = new window.ResizeObserver(scheduleStickyHeaderOffset);
        stickyHeaderObserver.observe(stickyHeader);
      }
    }

    const setFilterScrollLock = (locked) => {
      document.documentElement.classList.toggle('pps-filter-open', locked);
      document.body.classList.toggle('pps-filter-open', locked);
    };

    const setPrimeScrollLock = (locked) => {
      document.documentElement.classList.toggle('pps-prime-open', locked);
      document.body.classList.toggle('pps-prime-open', locked);
    };

    const closePrimeDialog = (shouldRestoreFocus = true) => {
      if (!elements.primeDialog || !elements.primeDialog.open) return;
      restorePrimeFocus = shouldRestoreFocus;
      elements.primeDialog.close();
    };

    const dismissPrimeStrip = () => {
      if (!elements.primeStrip) return;
      elements.primeStrip.hidden = true;
      try {
        window.sessionStorage.setItem(PRIME_DISMISS_KEY, 'true');
      } catch (error) {
        /* The in-page dismissal still succeeds when storage is unavailable. */
      }
    };

    const closeFilterSheet = (shouldRestoreFocus = true, preserveChoices = false) => {
      if (!elements.filterDialog || !elements.filterDialog.open) return;
      restoreFilterFocus = shouldRestoreFocus;
      preserveFilterChoicesOnClose = preserveChoices;
      elements.filterDialog.close();
    };

    const openFilterSheet = () => {
      if (!elements.filterDialog) return;

      closePrimeDialog(false);

      const ageGatePending = ageVerifier && !ageGateResolved &&
        (ageVerifier.dataset.testMode === 'true' || !hasAgeCookie());
      if (ageGatePending) {
        if (!ageVerifier.hasAttribute('open') && typeof ageVerifier.show === 'function') ageVerifier.show();
        return;
      }
      if (ageVerifier && ageVerifier.hasAttribute('open')) return;
      if (typeof elements.filterDialog.showModal !== 'function') return;

      lastFilterTrigger = elements.filterOpen;
      restoreFilterFocus = true;
      preserveFilterChoicesOnClose = false;
      if (!elements.filterDialog.open) elements.filterDialog.showModal();
      setFilterScrollLock(true);
      window.requestAnimationFrame(() => {
        if (elements.filterDialog && elements.filterDialog.open && elements.filterClose) {
          elements.filterClose.focus({ preventScroll: true });
        }
      });
    };

    const openPrimeDialog = () => {
      if (!elements.primeDialog) return;

      const ageGatePending = ageVerifier && !ageGateResolved &&
        (ageVerifier.dataset.testMode === 'true' || !hasAgeCookie());
      if (ageGatePending) {
        if (!ageVerifier.hasAttribute('open') && typeof ageVerifier.show === 'function') ageVerifier.show();
        return;
      }
      if (ageVerifier && ageVerifier.hasAttribute('open')) return;

      closeFilterSheet(false, true);
      if (typeof elements.primeDialog.showModal !== 'function') {
        const fallbackURL = elements.primeOpen && elements.primeOpen.dataset.fallbackUrl;
        if (fallbackURL) window.location.assign(fallbackURL);
        return;
      }

      lastPrimeTrigger = elements.primeOpen;
      restorePrimeFocus = true;
      if (!elements.primeDialog.open) elements.primeDialog.showModal();
      setPrimeScrollLock(true);
      window.requestAnimationFrame(() => {
        if (elements.primeDialog && elements.primeDialog.open && elements.primeClose) {
          elements.primeClose.focus({ preventScroll: true });
        }
      });
    };

    const abortRequest = (name) => {
      const controller = state[`${name}Controller`];
      if (controller && typeof controller.abort === 'function') controller.abort();
      state[`${name}Controller`] = null;
      state[`${name}Token`] += 1;
    };

    const beginRequest = (name) => {
      abortRequest(name);
      const token = state[`${name}Token`];
      const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      state[`${name}Controller`] = controller;
      return { controller, token };
    };

    const requestIsCurrent = (name, request) => (
      !state.destroyed && state[`${name}Token`] === request.token
    );

    const addToCache = (url, page) => {
      if (cache.size >= 24 && !cache.has(url)) {
        const oldestKey = cache.keys().next().value;
        cache.delete(oldestKey);
      }
      cache.set(url, page);
    };

    const makeCollectionURL = (handle) => {
      const url = new URL(allURL.toString());
      if (handle === 'all') {
        url.search = '';
        url.hash = '';
        return url;
      }
      const pathParts = url.pathname.split('/');
      const collectionsIndex = pathParts.lastIndexOf('collections');

      if (collectionsIndex >= 0) {
        pathParts[collectionsIndex + 1] = handle;
        pathParts.splice(collectionsIndex + 2);
        url.pathname = pathParts.join('/') || '/';
      } else {
        url.pathname = `/collections/${handle}`;
      }

      url.search = '';
      url.hash = '';
      return url;
    };

    const buildQueryURL = (filters, sort) => {
      const handle = filters.type === 'flower' && filters.tier ? filters.tier : filters.type;
      const url = makeCollectionURL(handle);

      url.searchParams.set('section_id', sectionId);
      url.searchParams.set('sort_by', sort || DEFAULT_SORT);
      if (filters.mood) url.searchParams.set('filter.p.m.custom.i_want_to_feel', filters.mood);
      if (filters.strain) url.searchParams.set('filter.p.m.custom.strain_type', filters.strain);
      return url.toString();
    };

    const normalizeNextURL = (value, sourceURL) => {
      if (!value) return '';

      try {
        const url = new URL(value, sourceURL);
        if (url.origin !== window.location.origin) return '';
        url.searchParams.set('section_id', sectionId);
        return url.toString();
      } catch (error) {
        return '';
      }
    };

    const fetchPage = async (url, controller) => {
      if (cache.has(url)) return cache.get(url);

      const options = { credentials: 'same-origin' };
      if (controller && controller.signal) options.signal = controller.signal;
      const response = await window.fetch(url, options);
      if (!response.ok) throw new Error(`Shop request failed with status ${response.status}`);

      const responseText = await response.text();
      const parsed = new DOMParser().parseFromString(responseText, 'text/html');
      const productContainer = parsed.querySelector('#ProductGridContainer');
      if (!productContainer) throw new Error('The product grid was missing from the shop response.');

      let sourceCards = Array.from(
        productContainer.querySelectorAll('motion-list > .product-card:not(.product-card--promo)')
      );
      if (sourceCards.length === 0) {
        sourceCards = Array.from(productContainer.querySelectorAll('.product-card:not(.product-card--promo)'));
      }

      if (excludePuffyPrime) sourceCards = sourceCards.filter((card) => !productCardIsPuffyPrime(card));
      const cards = sourceCards.map(cloneProductCard);
      const countElement = parsed.querySelector('#ProductCount') || parsed.querySelector('results-count');
      const parsedCount = parseCount(countElement, cards.length);
      const requestURL = new URL(url, window.location.origin);
      const sameCatalogPath = requestURL.pathname.replace(/\/$/, '') === allURL.pathname.replace(/\/$/, '');
      const hasProfileFilter = requestURL.searchParams.has('filter.p.m.custom.i_want_to_feel') ||
        requestURL.searchParams.has('filter.p.m.custom.strain_type');
      const totalOffset = excludePuffyPrime && sameCatalogPath && !hasProfileFilter ? 1 : 0;
      const pagination = productContainer.querySelector('.pagination') || parsed.querySelector('.pagination');
      const action = pagination ? pagination.querySelector('[action]') : null;
      const nextLink = pagination ? pagination.querySelector('a.next') : null;
      const nextValue = action ? action.getAttribute('action') : (nextLink ? nextLink.getAttribute('href') : '');
      const page = {
        url,
        cards,
        total: Math.max(0, parsedCount.value - totalOffset),
        countKnown: parsedCount.known,
        nextURL: normalizeNextURL(nextValue, url)
      };

      addToCache(url, page);
      return page;
    };

    const controlsForName = (name) => Array.from(form.elements).filter((control) => control.name === name);

    const selectedValue = (name, fallback) => {
      const selected = controlsForName(name).find((control) => control.checked);
      return selected ? selected.value : fallback;
    };

    const setSelectedValue = (name, value) => {
      const controls = controlsForName(name);
      let matched = false;

      controls.forEach((control) => {
        const checked = control.value === value;
        control.checked = checked;
        if (checked) matched = true;
      });

      if (!matched && controls.length > 0) {
        const fallback = controls.find((control) => control.value === '') || controls[0];
        fallback.checked = true;
      }
    };

    const hasControlValue = (name, value) => controlsForName(name).some((control) => control.value === value);

    const readFilters = () => {
      const typeValue = selectedValue('product_type', 'all');
      const type = allowedTypes.has(typeValue) ? typeValue : 'all';
      const tierValue = selectedValue('flower_tier', '');

      return {
        type,
        mood: selectedValue('mood', ''),
        strain: selectedValue('strain', ''),
        tier: type === 'flower' && allowedTiers.has(tierValue) ? tierValue : ''
      };
    };

    const filtersFromLocation = () => {
      const params = new URL(window.location.href).searchParams;
      const requestedType = params.get('type') || 'all';
      const type = allowedTypes.has(requestedType) && hasControlValue('product_type', requestedType)
        ? requestedType
        : 'all';
      const requestedMood = params.get('mood') || '';
      const requestedStrain = params.get('strain') || '';
      const requestedTier = params.get('tier') || '';

      return {
        type,
        mood: hasControlValue('mood', requestedMood) ? requestedMood : '',
        strain: hasControlValue('strain', requestedStrain) ? requestedStrain : '',
        tier: type === 'flower' && allowedTiers.has(requestedTier) && hasControlValue('flower_tier', requestedTier)
          ? requestedTier
          : ''
      };
    };

    const sortFromLocation = () => {
      const requestedSort = new URL(window.location.href).searchParams.get('sort') || DEFAULT_SORT;
      if (!elements.sort) return requestedSort || DEFAULT_SORT;
      return Array.from(elements.sort.options).some((option) => option.value === requestedSort)
        ? requestedSort
        : DEFAULT_SORT;
    };

    const updateTierRegion = (type, resetWhenHidden) => {
      const visible = type === 'flower';
      if (resetWhenHidden && !visible) setSelectedValue('flower_tier', '');

      if (elements.tierRegion) {
        elements.tierRegion.hidden = !visible;
        elements.tierRegion.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }

      elements.tierInputs.forEach((input) => {
        const wasDisabled = originalDisabled.get(input) || false;
        input.disabled = wasDisabled || !visible;
      });
    };

    const writeFiltersToForm = (filters) => {
      setSelectedValue('product_type', filters.type);
      setSelectedValue('mood', filters.mood);
      setSelectedValue('strain', filters.strain);
      setSelectedValue('flower_tier', filters.type === 'flower' ? filters.tier : '');
      updateTierRegion(filters.type, true);
    };

    const setSortControl = (sort) => {
      if (!elements.sort) return;
      const available = Array.from(elements.sort.options).some((option) => option.value === sort);
      elements.sort.value = available ? sort : DEFAULT_SORT;
    };

    const setApplyCount = (count, mode) => {
      if (!elements.applyCount) return;
      if (mode === 'loading') {
        elements.applyCount.textContent = '…';
        return;
      }
      if (mode === 'error') {
        elements.applyCount.textContent = '?';
        return;
      }
      elements.applyCount.textContent = String(count);
    };

    const refreshApplyDisabled = () => {
      setDisabled(elements.apply, state.mainBusy);
      if (elements.apply) {
        if (state.mainBusy) elements.apply.setAttribute('aria-busy', 'true');
        else elements.apply.removeAttribute('aria-busy');
      }
    };

    const announce = (message) => {
      if (!elements.status || !message) return;
      window.clearTimeout(state.announceTimer);
      elements.status.textContent = '';
      state.announceTimer = window.setTimeout(() => {
        if (!state.destroyed) elements.status.textContent = message;
      }, 20);
    };

    const directCards = () => Array.from(grid.children).filter((child) => (
      typeof child.matches === 'function' && child.matches('.product-card')
    ));

    const setResultMode = (mode) => {
      const hasCards = directCards().length > 0;
      if (elements.loading) {
        elements.loading.hidden = mode !== 'loading';
        elements.loading.setAttribute('aria-hidden', mode === 'loading' ? 'false' : 'true');
      }
      if (elements.empty) elements.empty.hidden = mode !== 'empty';
      if (elements.error) elements.error.hidden = mode !== 'error';
      grid.hidden = mode === 'loading' || mode === 'empty' || (mode === 'error' && !hasCards);
      results.dataset.ppShopState = mode;

      if (mode === 'loading') results.setAttribute('aria-busy', 'true');
      else results.removeAttribute('aria-busy');
    };

    const updateMoreControls = () => {
      const cards = directCards();
      const hiddenCards = cards.filter((card) => card.hidden);
      const visibleCount = cards.length - hiddenCards.length;
      const total = state.countKnown
        ? state.currentTotal
        : Math.max(state.currentTotal, visibleCount, cards.length + (state.currentNextURL ? pageSize : 0));
      const canLoadMore = hiddenCards.length > 0 || Boolean(state.currentNextURL) || state.needsInitialFetch;

      if (elements.progress) {
        elements.progress.textContent = `Showing ${visibleCount} of ${total} products`;
      }
      if (elements.more) {
        elements.more.hidden = !canLoadMore;
        setDisabled(elements.more, !canLoadMore || state.moreBusy || state.mainBusy);
      }
      if (elements.sentinel) elements.sentinel.hidden = !canLoadMore;

      if (elements.moreLabel && !state.moreBusy) elements.moreLabel.textContent = 'SEE MORE PRODUCTS';
      if (elements.sentinelLabel) {
        elements.sentinelLabel.textContent = state.moreBusy ? 'LOADING MORE PRODUCTS…' : 'MORE PRODUCTS LOAD AS YOU SCROLL';
      }

      return { visibleCount, total, hiddenCards };
    };

    const controlLabel = (name, value) => {
      const controlName = name === 'type' ? 'product_type' : (name === 'tier' ? 'flower_tier' : name);
      const control = controlsForName(controlName).find((candidate) => candidate.value === value);
      if (!control) return normalizeText(value).replace(/-/g, ' ');
      if (control.dataset.label) return normalizeText(control.dataset.label);

      const label = control.closest('label');
      if (!label) return normalizeText(value).replace(/-/g, ' ');
      const strong = label.querySelector('strong');
      if (strong) return normalizeText(strong.textContent);
      const spans = Array.from(label.querySelectorAll('span'));
      const textSpan = spans.find((span) => normalizeText(span.textContent));
      return textSpan ? normalizeText(textSpan.textContent) : normalizeText(value).replace(/-/g, ' ');
    };

    const updateCatalogHeading = (filters) => {
      if (!elements.headingMain || !elements.headingAccent) return;

      if (filters.type === 'all') {
        elements.headingMain.textContent = elements.headingMain.dataset.defaultText || 'SHOP ALL';
        elements.headingAccent.textContent = elements.headingAccent.dataset.defaultText || 'PRODUCTS.';
        return;
      }

      const categoryLabel = controlLabel('type', filters.type).replace(/[.!?]+$/, '');
      elements.headingMain.textContent = 'SHOP';
      elements.headingAccent.textContent = `${categoryLabel}.`;
    };

    const renderChips = (filters) => {
      updateCatalogHeading(filters);
      if (!elements.chips || !elements.summary) return;
      const chipData = [];
      if (filters.type !== 'all') chipData.push({ dimension: 'type', label: controlLabel('type', filters.type) });
      if (filters.type === 'flower' && filters.tier) chipData.push({ dimension: 'tier', label: controlLabel('tier', filters.tier) });
      if (filters.mood) chipData.push({ dimension: 'mood', label: controlLabel('mood', filters.mood) });
      if (filters.strain) chipData.push({ dimension: 'strain', label: controlLabel('strain', filters.strain) });

      elements.chips.textContent = '';
      const fragment = document.createDocumentFragment();
      chipData.forEach((chipDataItem) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pps-filter-chip';
        chip.dataset.ppShopChip = chipDataItem.dimension;
        chip.setAttribute('aria-label', `Remove ${chipDataItem.label} filter`);
        chip.textContent = `${chipDataItem.label} ×`;
        fragment.appendChild(chip);
      });
      elements.chips.appendChild(fragment);
      elements.summary.hidden = chipData.length === 0;
      if (elements.filterCount) {
        elements.filterCount.textContent = String(chipData.length);
        elements.filterCount.hidden = chipData.length === 0;
      }
      if (elements.filterOpen) {
        elements.filterOpen.setAttribute(
          'aria-label',
          chipData.length === 0
            ? 'Filter products'
            : `Filter products, ${chipData.length} active ${chipData.length === 1 ? 'filter' : 'filters'}`
        );
      }
      root.classList.toggle('has-profile-filters', Boolean(filters.mood || filters.strain));
    };

    const updateFriendlyURL = (filters, sort) => {
      if (!window.history || typeof window.history.pushState !== 'function') return;

      try {
        const url = new URL(window.location.href);
        FRIENDLY_PARAMS.forEach((parameter) => url.searchParams.delete(parameter));
        url.searchParams.delete('section_id');
        url.searchParams.delete('sort_by');
        url.searchParams.delete('filter.p.m.custom.i_want_to_feel');
        url.searchParams.delete('filter.p.m.custom.strain_type');
        url.searchParams.delete('filter.v.price.lte');
        url.searchParams.delete('page');

        if (filters.type !== 'all') url.searchParams.set('type', filters.type);
        if (filters.mood) url.searchParams.set('mood', filters.mood);
        if (filters.strain) url.searchParams.set('strain', filters.strain);
        if (filters.type === 'flower' && filters.tier) url.searchParams.set('tier', filters.tier);
        if (sort !== DEFAULT_SORT) url.searchParams.set('sort', sort);
        if (url.toString() !== window.location.href) {
          window.history.pushState(window.history.state, '', url.toString());
        }
      } catch (error) {
        // A locked-down preview can reject history updates without affecting shopping.
      }
    };

    const stickyHeaderOffset = () => {
      const header = document.querySelector(
        '.header-section.header-sticky, header.header[is="sticky-header"], .header-section'
      );
      if (!header) return 16;

      const rect = header.getBoundingClientRect();
      const style = window.getComputedStyle ? window.getComputedStyle(header) : null;
      const isSticky = header.classList.contains('header-sticky') ||
        (style && (style.position === 'fixed' || style.position === 'sticky'));
      return isSticky && rect.bottom > 0 ? Math.ceil(rect.bottom) + 16 : 16;
    };

    const scrollToResults = () => {
      if (typeof window.scrollTo !== 'function') return;
      const run = () => {
        if (state.destroyed || ('isConnected' in results && !results.isConnected)) return;
        const top = Math.max(0, results.getBoundingClientRect().top + window.pageYOffset - stickyHeaderOffset());
        const reducedMotion = typeof window.matchMedia === 'function' &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        try {
          window.scrollTo({ top, behavior: reducedMotion ? 'auto' : 'smooth' });
        } catch (error) {
          window.scrollTo(0, top);
        }
      };

      if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(run);
      else window.setTimeout(run, 0);
    };

    const notifyProductUpgrade = () => {
      if (typeof window.CustomEvent !== 'function') return;
      document.dispatchEvent(new window.CustomEvent('collection:reloaded', {
        detail: { source: 'puffy-concept7-shop', root }
      }));
    };

    const renderBasePage = (page, filters, sort, options) => {
      const total = Math.max(page.total, page.cards.length);
      const fragment = document.createDocumentFragment();

      page.cards.forEach((template, index) => {
        const card = cloneProductCard(template);
        card.hidden = index >= pageSize;
        fragment.appendChild(card);
      });

      grid.textContent = '';
      grid.appendChild(fragment);
      state.renderGeneration += 1;
      state.currentTotal = total;
      state.countKnown = page.countKnown;
      state.currentNextURL = page.nextURL;
      state.currentQueryURL = page.url;
      state.loadedPages = new Set([page.url]);
      state.needsInitialFetch = false;
      state.applied = cloneFilters(filters);
      state.appliedSort = sort;
      state.retryAction = null;

      if (elements.resultCount) elements.resultCount.textContent = String(total);
      renderChips(state.applied);
      setResultMode(total === 0 || page.cards.length === 0 ? 'empty' : 'ready');
      const progress = updateMoreControls();
      rearmInfiniteScroll();
      notifyProductUpgrade();

      if (filtersEqual(state.pending, filters)) setApplyCount(total);
      if (options.updateHistory) updateFriendlyURL(filters, sort);
      if (options.scroll) scrollToResults();
      if (options.announce) {
        announce(`${total} matching ${total === 1 ? 'product' : 'products'} loaded. Showing ${progress.visibleCount} of ${progress.total}.`);
      }
    };

    const clearPreflight = () => {
      window.clearTimeout(state.preflightTimer);
      state.preflightTimer = 0;
      abortRequest('preflight');
      state.preflightBusy = false;
      refreshApplyDisabled();
    };

    const showRequestError = (message, retryAction) => {
      state.retryAction = retryAction;
      setResultMode('error');
      setDisabled(elements.retry, false);
      announce(message);
    };

    const applyFilters = async (filters, sort, options) => {
      const queryURL = buildQueryURL(filters, sort);
      clearPreflight();
      abortRequest('initial');
      abortRequest('more');
      state.moreBusy = false;
      if (elements.more) elements.more.removeAttribute('aria-busy');
      abortRequest('main');
      state.mainBusy = false;
      refreshApplyDisabled();
      state.renderGeneration += 1;

      if (cache.has(queryURL)) {
        renderBasePage(cache.get(queryURL), filters, sort, options);
        return;
      }

      const request = beginRequest('main');
      state.mainBusy = true;
      refreshApplyDisabled();
      setResultMode('loading');
      updateMoreControls();

      try {
        const page = await fetchPage(queryURL, request.controller);
        if (!requestIsCurrent('main', request)) return;
        renderBasePage(page, filters, sort, options);
        state.retryAction = null;
      } catch (error) {
        if (!isAbortError(error) && requestIsCurrent('main', request)) {
          showRequestError(
            'We could not refresh the products. Your choices are still saved.',
            () => applyFilters(filters, sort, options)
          );
        }
      } finally {
        if (requestIsCurrent('main', request)) {
          state.mainController = null;
          state.mainBusy = false;
          refreshApplyDisabled();
          updateMoreControls();
        }
      }
    };

    const focusFirstRevealedTitle = (cards) => {
      const firstCard = cards[0];
      if (!firstCard) return;
      const title = firstCard.querySelector('.product-card__title');
      if (!title || typeof title.focus !== 'function') return;
      try {
        title.focus({ preventScroll: true });
      } catch (error) {
        title.focus();
      }
    };

    const revealBufferedCards = (keyboardActivation) => {
      const hiddenCards = directCards().filter((card) => card.hidden).slice(0, pageSize);
      hiddenCards.forEach((card) => {
        card.hidden = false;
      });
      const progress = updateMoreControls();

      if (hiddenCards.length > 0) {
        announce(`Loaded ${hiddenCards.length} more ${hiddenCards.length === 1 ? 'product' : 'products'}. Showing ${progress.visibleCount} of ${progress.total}.`);
        if (keyboardActivation) focusFirstRevealedTitle(hiddenCards);
      }
      return hiddenCards.length;
    };

    const loadMore = async (keyboardActivation) => {
      if (state.moreBusy || state.mainBusy) return;
      if (state.needsInitialFetch) {
        const initialLoad = applyFilters(cloneFilters(state.applied), state.appliedSort, {
          updateHistory: false,
          scroll: false,
          announce: false
        });
        state.moreBusy = true;
        setDisabled(elements.more, true);
        if (elements.more) elements.more.setAttribute('aria-busy', 'true');
        if (elements.moreLabel) elements.moreLabel.textContent = 'LOADING…';
        if (elements.sentinelLabel) elements.sentinelLabel.textContent = 'LOADING MORE PRODUCTS…';

        try {
          await initialLoad;
          if (!state.needsInitialFetch) revealBufferedCards(keyboardActivation);
        } finally {
          state.moreBusy = false;
          if (elements.more) elements.more.removeAttribute('aria-busy');
          updateMoreControls();
        }
        return;
      }
      if (revealBufferedCards(keyboardActivation) > 0) return;
      if (!state.currentNextURL) {
        updateMoreControls();
        return;
      }

      const generation = state.renderGeneration;
      const request = beginRequest('more');
      state.moreBusy = true;
      setDisabled(elements.more, true);
      if (elements.more) elements.more.setAttribute('aria-busy', 'true');
      if (elements.moreLabel) elements.moreLabel.textContent = 'LOADING…';
      if (elements.sentinelLabel) elements.sentinelLabel.textContent = 'LOADING MORE PRODUCTS…';
      if (elements.error) elements.error.hidden = true;

      try {
        const visitedThisLoad = new Set();
        let nextURL = state.currentNextURL;
        let cardsAdded = 0;
        let pageHops = 0;
        const maxPageHops = 10;

        while (nextURL && cardsAdded === 0 && pageHops < maxPageHops) {
          if (state.loadedPages.has(nextURL) || visitedThisLoad.has(nextURL)) {
            state.currentNextURL = '';
            break;
          }

          visitedThisLoad.add(nextURL);
          pageHops += 1;
          const page = await fetchPage(nextURL, request.controller);
          if (!requestIsCurrent('more', request) || generation !== state.renderGeneration) return;

          state.loadedPages.add(nextURL);
          state.currentNextURL = page.nextURL && !state.loadedPages.has(page.nextURL) ? page.nextURL : '';
          nextURL = state.currentNextURL;

          if (page.countKnown) {
            state.currentTotal = Math.max(page.total, directCards().length);
            state.countKnown = true;
            if (elements.resultCount) elements.resultCount.textContent = String(state.currentTotal);
          }

          if (page.cards.length > 0) {
            const fragment = document.createDocumentFragment();
            page.cards.forEach((template) => {
              const card = cloneProductCard(template);
              card.hidden = true;
              fragment.appendChild(card);
            });
            grid.appendChild(fragment);
            cardsAdded = page.cards.length;
          }
        }

        setResultMode(directCards().length > 0 ? 'ready' : 'empty');
        if (cardsAdded > 0) {
          notifyProductUpgrade();
          revealBufferedCards(keyboardActivation);
        } else if (state.currentNextURL) {
          announce('No products were found in the next catalog pages. Select See More to keep looking.');
        } else {
          announce('All matching products are already showing.');
        }
        state.retryAction = null;
      } catch (error) {
        if (!isAbortError(error) && requestIsCurrent('more', request)) {
          showRequestError(
            'We could not load more products. Try again when you are ready.',
            () => loadMore(keyboardActivation)
          );
        }
      } finally {
        if (requestIsCurrent('more', request)) {
          state.moreController = null;
          state.moreBusy = false;
          if (elements.more) elements.more.removeAttribute('aria-busy');
          updateMoreControls();
        }
      }
    };

    const applyPending = (scroll) => {
      state.pending = readFilters();
      applyFilters(cloneFilters(state.pending), state.sort, {
        updateHistory: true,
        scroll: Boolean(scroll),
        announce: true
      });
    };

    const resetAll = () => {
      const defaults = { type: 'all', mood: '', strain: '', tier: '' };
      state.sort = DEFAULT_SORT;
      setSortControl(DEFAULT_SORT);
      writeFiltersToForm(defaults);
      state.pending = cloneFilters(defaults);
      applyFilters(defaults, DEFAULT_SORT, {
        updateHistory: true,
        scroll: false,
        announce: true
      });
    };

    const removeChip = (dimension) => {
      const next = cloneFilters(state.applied);
      if (dimension === 'type') {
        next.type = 'all';
        next.tier = '';
      } else if (dimension === 'tier') {
        next.tier = '';
      } else if (dimension === 'mood') {
        next.mood = '';
      } else if (dimension === 'strain') {
        next.strain = '';
      } else {
        return;
      }

      writeFiltersToForm(next);
      state.pending = cloneFilters(next);
      applyFilters(next, state.sort, {
        updateHistory: true,
        scroll: false,
        announce: true
      });
    };

    const updateMoodDisclosure = (expanded) => {
      state.moodsExpanded = Boolean(expanded);
      elements.extraMoods.forEach((item) => {
        const selected = Boolean(item.querySelector('input:checked'));
        item.hidden = !state.moodsExpanded && !selected;
      });
      if (!elements.moodsToggle) return;

      elements.moodsToggle.setAttribute('aria-expanded', state.moodsExpanded ? 'true' : 'false');
      const children = Array.from(elements.moodsToggle.children);
      const label = children[0] || elements.moodsToggle;
      label.textContent = state.moodsExpanded ? 'SHOW FEWER MOODS' : 'SEE ALL MOODS';
      const icon = children.find((child) => child.getAttribute('aria-hidden') === 'true');
      if (icon) icon.textContent = state.moodsExpanded ? '−' : '＋';
    };

    const handleFilterChange = (event) => {
      const target = event.target;
      if (!target || typeof target.matches !== 'function') return;
      if (!target.matches(`${SELECTORS.filter}, ${SELECTORS.tier}`)) return;

      if (target.name === 'product_type') updateTierRegion(target.value, true);
      state.pending = readFilters();
      clearPreflight();
      if (elements.applyCount) elements.applyCount.textContent = '→';

      if (target.name === 'product_type') {
        applyFilters(cloneFilters(state.pending), state.sort, {
          updateHistory: true,
          scroll: false,
          announce: true
        });
      }
    };

    const handleSortChange = () => {
      const value = elements.sort ? elements.sort.value : DEFAULT_SORT;
      state.sort = value || DEFAULT_SORT;
      clearPreflight();
      applyFilters(cloneFilters(state.applied), state.sort, {
        updateHistory: true,
        scroll: false,
        announce: true
      });
    };

    const preloadDefaultResults = () => {
      if (
        state.destroyed ||
        !state.needsInitialFetch ||
        state.initialController ||
        state.mainBusy ||
        state.moreBusy
      ) return;

      const filters = cloneFilters(state.applied);
      const sort = state.appliedSort;
      const queryURL = buildQueryURL(filters, sort);
      const request = beginRequest('initial');

      fetchPage(queryURL, request.controller)
        .then((page) => {
          if (!requestIsCurrent('initial', request)) return;
          if (
            !state.needsInitialFetch ||
            state.mainBusy ||
            state.moreBusy ||
            !filtersEqual(state.applied, filters) ||
            state.sort !== sort
          ) return;

          renderBasePage(page, filters, sort, {
            updateHistory: false,
            scroll: false,
            announce: false
          });
        })
        .catch((error) => {
          if (!requestIsCurrent('initial', request) || isAbortError(error)) return;
          if (window.console && typeof window.console.warn === 'function') {
            window.console.warn('Puffy Parcel could not preload the best-selling shelf; keeping Liquid products.', error);
          }
          state.sort = catalogDefaultSort;
          state.appliedSort = catalogDefaultSort;
          setSortControl(catalogDefaultSort);
          setResultMode(directCards().length > 0 ? 'ready' : 'empty');
          updateMoreControls();
        })
        .finally(() => {
          if (requestIsCurrent('initial', request)) state.initialController = null;
        });
    };

    const armDefaultPreload = () => {
      if (typeof window.IntersectionObserver !== 'function' || !state.needsInitialFetch) return false;

      initialObserver = new window.IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        initialObserver.disconnect();
        initialObserver = null;
        preloadDefaultResults();
      }, { rootMargin: '900px 0px' });
      initialObserver.observe(results);
      return true;
    };

    const rearmInfiniteScroll = () => {
      if (!infiniteObserver || !elements.sentinel || state.destroyed) return;
      infiniteObserver.unobserve(elements.sentinel);
      if (infiniteRearmFrame) window.cancelAnimationFrame(infiniteRearmFrame);
      if (results.dataset.ppShopState === 'error') return;

      infiniteRearmFrame = window.requestAnimationFrame(() => {
        infiniteRearmFrame = 0;
        if (!state.destroyed && infiniteObserver && elements.sentinel && !elements.sentinel.hidden) {
          infiniteObserver.observe(elements.sentinel);
        }
      });
    };

    const armInfiniteScroll = () => {
      if (
        !elements.more ||
        !elements.sentinel ||
        typeof window.IntersectionObserver !== 'function'
      ) return false;

      root.classList.add('is-infinite-scroll');
      infiniteObserver = new window.IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        if (
          state.destroyed ||
          state.moreBusy ||
          state.mainBusy ||
          elements.sentinel.hidden ||
          results.dataset.ppShopState === 'error'
        ) return;

        infiniteObserver.unobserve(elements.sentinel);
        loadMore(false).finally(rearmInfiniteScroll);
      }, { rootMargin: '500px 0px', threshold: 0.01 });
      infiniteObserver.observe(elements.sentinel);
      return true;
    };

    on(form, 'change', handleFilterChange);
    on(form, 'submit', (event) => {
      event.preventDefault();
      closeFilterSheet(true, true);
      applyPending(false);
    });
    on(elements.apply, 'click', (event) => {
      event.preventDefault();
      closeFilterSheet(true, true);
      applyPending(false);
    });
    elements.resets.forEach((button) => on(button, 'click', (event) => {
      event.preventDefault();
      resetAll();
      closeFilterSheet(true, true);
    }));
    on(elements.filterOpen, 'click', (event) => {
      event.preventDefault();
      openFilterSheet();
    });
    on(elements.filterClose, 'click', (event) => {
      event.preventDefault();
      closeFilterSheet(true);
    });
    on(elements.filterDialog, 'click', (event) => {
      if (event.target === elements.filterDialog) closeFilterSheet(true);
    });
    on(elements.filterDialog, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeFilterSheet(true);
    });
    on(elements.filterDialog, 'close', () => {
      setFilterScrollLock(false);
      if (!preserveFilterChoicesOnClose && state.applied) {
        writeFiltersToForm(state.applied);
        state.pending = cloneFilters(state.applied);
        setApplyCount(state.currentTotal);
      }
      preserveFilterChoicesOnClose = false;
      if (restoreFilterFocus && lastFilterTrigger && document.contains(lastFilterTrigger)) {
        lastFilterTrigger.focus({ preventScroll: true });
      }
    });
    on(elements.primeOpen, 'click', (event) => {
      event.preventDefault();
      openPrimeDialog();
    });
    on(elements.primeDismiss, 'click', (event) => {
      event.preventDefault();
      dismissPrimeStrip();
    });
    on(elements.primeClose, 'click', (event) => {
      event.preventDefault();
      closePrimeDialog(true);
    });
    on(elements.primeDialog, 'click', (event) => {
      if (event.target === elements.primeDialog) closePrimeDialog(true);
    });
    on(elements.primeDialog, 'cancel', () => {
      restorePrimeFocus = true;
    });
    on(elements.primeDialog, 'close', () => {
      setPrimeScrollLock(false);
      if (elements.primeVideo && typeof elements.primeVideo.pause === 'function') elements.primeVideo.pause();
      if (restorePrimeFocus && lastPrimeTrigger && document.contains(lastPrimeTrigger)) {
        lastPrimeTrigger.focus({ preventScroll: true });
      }
    });
    on(document, 'toggle', (event) => {
      if (event.target && event.target.id === 'AgeVerifier' && event.target.hasAttribute('open')) {
        closeFilterSheet(false);
        closePrimeDialog(false);
      }
    });
    on(document, 'click', (event) => {
      if (event.target && event.target.closest('#AgeVerifier .cookie__buttons button[aria-controls="AgeVerifier"]')) {
        ageGateResolved = true;
      }
    });
    on(elements.sort, 'change', handleSortChange);
    on(elements.more, 'click', (event) => {
      event.preventDefault();
      loadMore(event.detail === 0);
    });
    on(elements.retry, 'click', (event) => {
      event.preventDefault();
      const retryAction = state.retryAction;
      if (typeof retryAction === 'function') Promise.resolve(retryAction()).finally(rearmInfiniteScroll);
    });
    on(elements.moodsToggle, 'click', (event) => {
      event.preventDefault();
      updateMoodDisclosure(!state.moodsExpanded);
    });
    on(elements.chips, 'click', (event) => {
      const target = event.target;
      const chip = target && typeof target.closest === 'function' ? target.closest('[data-pp-shop-chip]') : null;
      if (!chip || !elements.chips.contains(chip)) return;
      event.preventDefault();
      removeChip(chip.dataset.ppShopChip);
    });
    on(window, 'popstate', () => {
      const filters = filtersFromLocation();
      const sort = sortFromLocation();
      state.sort = sort;
      setSortControl(sort);
      writeFiltersToForm(filters);
      state.pending = cloneFilters(filters);
      applyFilters(filters, sort, {
        updateHistory: false,
        scroll: false,
        announce: true
      });
    });

    const initialFilters = filtersFromLocation();
    const initialSort = sortFromLocation();
    state.pending = cloneFilters(initialFilters);
    state.applied = cloneFilters(initialFilters);
    state.sort = initialSort;
    state.appliedSort = initialSort;
    setSortControl(initialSort);
    writeFiltersToForm(initialFilters);
    renderChips(initialFilters);
    const selectedExtraMood = elements.extraMoods.some((item) => Boolean(item.querySelector('input:checked')));
    updateMoodDisclosure(selectedExtraMood);

    root.dataset.ppShopEnhanced = 'true';
    armInfiniteScroll();

    const hasInitialSelection = initialFilters.type !== 'all' ||
      Boolean(initialFilters.mood) ||
      Boolean(initialFilters.strain) ||
      Boolean(initialFilters.tier) ||
      initialSort !== DEFAULT_SORT;

    if (hasInitialSelection) {
      applyFilters(initialFilters, initialSort, {
          updateHistory: false,
          scroll: false,
          announce: false
      });
    } else {
      const initialCount = parseCount(elements.resultCount, directCards().length);
      state.currentTotal = initialCount.value;
      state.countKnown = initialCount.known;
      setResultMode(directCards().length > 0 ? 'loading' : 'empty');
      setApplyCount(state.currentTotal);
      updateMoreControls();
      if (!armDefaultPreload()) {
        state.sort = catalogDefaultSort;
        state.appliedSort = catalogDefaultSort;
        setSortControl(catalogDefaultSort);
        setResultMode(directCards().length > 0 ? 'ready' : 'empty');
      }
    }

    root.ppShopCleanup = () => {
      if (state.destroyed) return;
      closeFilterSheet(false, true);
      closePrimeDialog(false);
      setFilterScrollLock(false);
      setPrimeScrollLock(false);
      if (elements.primeVideo && typeof elements.primeVideo.pause === 'function') elements.primeVideo.pause();
      state.destroyed = true;
      window.clearTimeout(state.preflightTimer);
      window.clearTimeout(state.announceTimer);
      if (initialObserver) initialObserver.disconnect();
      initialObserver = null;
      if (infiniteObserver) infiniteObserver.disconnect();
      infiniteObserver = null;
      if (infiniteRearmFrame) window.cancelAnimationFrame(infiniteRearmFrame);
      infiniteRearmFrame = 0;
      root.classList.remove('is-infinite-scroll');
      if (stickyHeaderObserver) stickyHeaderObserver.disconnect();
      stickyHeaderObserver = null;
      if (stickyHeaderFrame) window.cancelAnimationFrame(stickyHeaderFrame);
      stickyHeaderFrame = 0;
      root.style.removeProperty('--pps-sticky-header-height');
      ['preflight', 'main', 'more', 'initial'].forEach(abortRequest);
      listeners.forEach(({ target, type, handler, options }) => {
        target.removeEventListener(type, handler, options);
      });
      [elements.apply, elements.sort, elements.more, elements.retry]
        .concat(elements.tierInputs)
        .forEach((element) => {
          if (element && 'disabled' in element && originalDisabled.has(element)) {
            element.disabled = originalDisabled.get(element);
          }
        });
      listeners.length = 0;
      cache.clear();
      delete root.dataset.ppShopEnhanced;
      delete root.ppShopCleanup;
    };
  };

  const initializeAll = (scope) => rootsWithin(scope).forEach(initializeShop);
  const cleanupAll = (scope) => rootsWithin(scope).forEach((root) => {
    if (typeof root.ppShopCleanup === 'function') root.ppShopCleanup();
  });

  window[GLOBAL_KEY] = { initializeAll, cleanupAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document), { once: true });
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupAll(event.target));
})();
